import {
  usePartnerShortQuery,
  usePracticeShortQuery,
  useProviderClinicalShortQuery,
  useProviderRegionShortQuery,
  useProvidersShortQuery,
} from '@common/cache';
import { useProviderTableStore } from '../filters.store';
import { isEqual, omit } from 'lodash';
import { SelectChangeEvent } from '@mui/material';
import { IProviderTableFilters } from '../../types';
import { useState } from 'react';

const certifiedOptions = [
  { name: 'Certified', value: 'true' },
  { name: 'Not certified', value: 'false' },
];

const providerSpecialtyOptions = [
  { label: 'Cardiology', value: 'Cardiology' },
  { label: 'Geropsych', value: 'Geropsych' },
  { label: 'Palliative care', value: 'Palliative care' },
  { label: 'Primary care', value: 'Primary care' },
  { label: 'Neurology', value: 'Neurology' },
  { label: 'Social Worker', value: 'Social Worker' },
];

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

export const useFiltersCard = () => {
  const {
    filters,
    draftFilters,
    setDraftFilters,
    handleToggleFiltersCard,
    resetFilters,
    applyDraftFilters,
    draftFiltersCount,
  } = useProviderTableStore();

  const { data: partnerShortOptions = [] } = usePartnerShortQuery();
  const { data: practiceShortOptions = [] } = usePracticeShortQuery({});

  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [clinicalLeaderSearchQuery, setClinicalLeaderSearchQuery] =
    useState('');
  const [supervisingPhysicianSearchQuery, setSupervisingPhysicianSearchQuery] =
    useState('');

  const { data: regionOptions = [], isLoading: isRegionLoading } =
    useProviderRegionShortQuery(regionSearchQuery || undefined);
  const {
    data: clinicalLeaderOptions = [],
    isLoading: isClinicalLeaderLoading,
  } = useProviderClinicalShortQuery(clinicalLeaderSearchQuery || undefined);
  const {
    data: supervisingPhysicianOptions = [],
    isLoading: isSupervisingPhysicianLoading,
  } = useProvidersShortQuery({
    search: supervisingPhysicianSearchQuery || undefined,
    // disabled:
    //   !supervisingPhysicianSearchQuery ||
    //   supervisingPhysicianSearchQuery.length < 3,
  });

  const handleChangeCertifiedFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({
      ...draftFilters,
      guide_certified: e.target.value,
      page: 1,
    });
  };

  const handleChangeStateFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({ state: e.target.value, page: 1 });
  };

  const handleChangePartnerFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({ partner_id: e.target.value, page: 1 });
  };

  const handleChangePracticeFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({ practice_id: e.target.value, page: 1 });
  };

  const handleChangeStatusFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({ status: e.target.value, page: 1 });
  };

  const handleChangeProviderSpecialityFilter = (
    e: SelectChangeEvent<string>,
  ) => {
    setDraftFilters({
      provider_specialty: e.target
        .value as IProviderTableFilters['provider_specialty'],
      page: 1,
    });
  };

  const handleChangeRegionFilter = (newValues: string[]) => {
    setDraftFilters({ region_ids: newValues, page: 1 });
  };

  const handleChangeClinicalLeaderFilter = (newValues: string[]) => {
    setDraftFilters({ clinical_leader_ids: newValues, page: 1 });
  };

  const handleChangeSupervisingPhysicianFilter = (newValues: string[]) => {
    setDraftFilters({ supervising_physician_ids: newValues, page: 1 });
  };

  const isResetDisabled = draftFiltersCount === 0;
  const isApplyDisabled = isEqual(
    omit(draftFilters, ['search', 'page', 'limit']),
    omit(filters, ['search', 'page', 'limit']),
  );

  return {
    filters: draftFilters,
    partnerOptions: partnerShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    practiceOptions: practiceShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    certifiedOptions: certifiedOptions.map(o => ({
      value: o.value,
      label: o.name,
    })),
    statusOptions,
    providerSpecialtyOptions,
    resetFilters,
    handleApply: applyDraftFilters,
    isResetDisabled,
    isApplyDisabled,
    handleToggleFiltersCard,
    handleChangeCertifiedFilter,
    handleChangePartnerFilter,
    handleChangePracticeFilter,
    handleChangeProviderSpecialityFilter,
    handleChangeStateFilter,
    handleChangeStatusFilter,
    regionOptions: regionOptions.map(o => ({ value: o.id, label: o.name })),
    clinicalLeaderOptions: clinicalLeaderOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    supervisingPhysicianOptions: supervisingPhysicianOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    isRegionLoading,
    isClinicalLeaderLoading,
    isSupervisingPhysicianLoading,
    regionSearchQuery,
    clinicalLeaderSearchQuery,
    supervisingPhysicianSearchQuery,
    setRegionSearchQuery,
    setClinicalLeaderSearchQuery,
    setSupervisingPhysicianSearchQuery,
    handleChangeRegionFilter,
    handleChangeClinicalLeaderFilter,
    handleChangeSupervisingPhysicianFilter,
  };
};
